import tinycolor from 'tinycolor2';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { theme } from '@zendesk/forethought-elements';
import {
  ApiConfiguration,
  AvailableVoice,
  BannerImageConfig,
  EmailConfiguration,
  GeneralInstructionConfig,
  KnowledgeConfiguration,
  PhoneNumberConfig,
  PhoneNumberProvider,
  PlaybookConfiguration,
  SlackConfiguration,
  SolveConfigState,
  SolveKnowledgeSource,
  ToneOfVoiceConfig,
  VoiceConfiguration,
  WebCallConfigData,
  WidgetConfiguration,
} from './types';
import omit from 'lodash/fp/omit';
import set from 'lodash/set';
import agentChatImageDefaultIcon from 'src/assets/images/agent-chat-image-default.svg';
import { isUrlValid } from 'src/components/app/helpers';
import { validateKnowledgeConfigurationPrompt } from 'src/components/knowledge-configuration-prompt-text-field/helpers';
import { validateGeneralInstructionConfig } from 'src/pages/solve-config/general-ai-instruction-tab-content/helpers';
import { TTS_SPEED } from 'src/pages/solve-config/voice/constants';
import { EmailResponseWidth } from 'src/services/email-settings/types';
import * as API from 'src/services/solve-config/solveConfigApi';
import type { RootState } from 'src/store/rootReducer';
import { isPhoneNumberValid } from 'src/utils/validators';

const initialState: SolveConfigState = {
  api: {
    data: {
      general_instruction_config: {
        general_context: '',
        glossary: '',
        is_enabled: false,
      },
      is_draft: true,
      is_modified: false,

      tone_of_voice_config: {
        custom_prompt: null,
        enabled: false,
        predefined_tones: [],
        should_use_predefined_tones: false,
      },
    },
    error: null,
    liveVersionExists: false,
    loading: false,
  },
  brandId: '',
  email: {
    data: {
      general_instruction_config: {
        general_context: '',
        glossary: '',
        is_enabled: false,
      },
    },
    loading: false,
  },
  emailConfigurationFields: {
    field_values: null,
    isFormValid: true,
    isModified: false,
    modified_date: null,
  },
  emailKnowledgeConfigurationPrompt: '',
  emailSettingsFormValues: null,
  emailToneOfVoiceConfig: {
    custom_prompt: null,
    enabled: false,
    predefined_tones: [],
    should_use_predefined_tones: false,
  },
  isDiscarding: false,
  isInitialized: false,
  isLoadingInitialConfig: true,
  isLongerNoteEnabled: false,
  isPublishing: false,
  isSolveLiteEnabled: false,
  knowledge: {
    data: {
      available_knowledge_sources: [],
      is_modified: false,
      selected_knowledge_source: undefined,
    },
    error: null,
    loading: false,
  },
  liveVersionExists: false,
  liveVersionIsActive: false,
  playbook: {
    data: {
      general_config: {
        auto_open_generated_reply: true,
      },
      general_instruction_config: {
        general_context: '',
        glossary: '',
        is_enabled: false,
      },
      is_draft: true,
      is_modified: false,
      knowledge_configuration_prompt: '',
      quick_feedback_config: {
        feedback_prompt: '',
        hover_label_bad: '',
        hover_label_good: '',
        is_enabled: false,
        negative_routing_intent_workflow_id: '',
        positive_routing_intent_workflow_id: null,
        question_for_high_ratings: '',
        question_for_low_ratings: '',
        should_request_feedback_for_high_ratings: false,
        should_request_feedback_for_low_ratings: false,
        style: '',
      },
      reply_generation_configuration_prompt: '',
      reply_generation_general_instruction_config: {
        general_context: '',
        glossary: '',
        is_enabled: false,
      },
      reply_generation_tone_of_voice_config: {
        custom_prompt: null,
        enabled: false,
        predefined_tones: [],
        should_use_predefined_tones: false,
      },
      tone_of_voice_config: {
        custom_prompt: null,
        enabled: false,
        predefined_tones: [],
        should_use_predefined_tones: false,
      },
    },
    error: null,
    isLoadingInitialConfig: true,
    liveVersionExists: false,
    loading: false,
  },
  slack: {
    data: {
      general_instruction_config: {
        general_context: '',
        glossary: '',
        is_enabled: false,
      },
      is_draft: true,
      is_modified: false,

      tone_of_voice_config: {
        custom_prompt: null,
        enabled: false,
        predefined_tones: [],
        should_use_predefined_tones: false,
      },
    },
    error: null,
    liveVersionExists: false,
    loading: false,
  },
  uiLoadingState: {
    isGenericFieldsLoading: false,
    isHighFrequencyFieldsLoading: false,
    isSwitchingConfig: false,
  },
  validationState: {
    isWarningModalVisible: false,
  },
  voice: {
    available_voices: [],
    bought_phone_numbers: [],
    data: {
      action_sound_id: 'typewriter',
      agent_name: '',
      agent_voice_id: '',
      company_name: '',
      csat_config: {
        is_enabled: true,
        negative_rating_routing_intent: undefined,
        no_resolve_option: '',
        one_to_five_labels: [],
        options_for_resolution_confirmation: [],
        question_for_additional_feedback: '',
        question_for_high_ratings: '',
        question_for_low_ratings: '',
        question_for_resolution_confirmation: '',
        rating_question: '',
        reasons_for_high_ratings: [],
        reasons_for_low_ratings: [],
        scale: 'one_to_five',
        should_request_additional_feedback: false,
        should_request_feedback_for_high_ratings: false,
        should_request_feedback_for_low_ratings: false,
        should_request_resolution_confirmation: false,
        should_show_submit_confirmation_message: false,
        should_use_rating_based_submit_message: false,
        show_csat_after_n_interactions: 1,
        show_on_header: false,
        show_when_agent_chat_ends: false,
        show_when_widget_minimizes: false,
        style: 'star',
        submit_confirmation_message: '',
        submit_message_for_high_ratings: '',
        submit_message_for_low_ratings: '',
        yes_resolve_option: '',
      },
      default_language: '',
      disclaimer_text: '',
      general_instruction_config: {
        general_context: '',
        glossary: '',
        is_enabled: false,
      },
      greeting_message: '',
      is_call_recording_enabled: false,
      is_disclaimer_enabled: false,
      is_draft: false,
      is_modified: false,
      language_config: {
        allowed_languages: [],
        default_language: 'en',
        language_voice_overrides: {},
        multilingual_enabled: false,
      },
      language_voice_overrides: {},
      org_id: -1,
      outbound_disclaimer_text: '',
      phone_number: '',
      play_action_sound_enabled: false,
      tone_of_voice_config: {
        custom_prompt: null,
        enabled: false,
        predefined_tones: [],
        should_use_predefined_tones: false,
      },
      tts_speed: TTS_SPEED.NORMAL,
      use_only_default_language: false,
    },
    error: null,
    is_customer_twilio_account: false,
    loading: false,
    selectedPhoneNumber: null,
  },
  webCall: {
    data: null,
    isLoading: false,
    isModified: false,
    liveVersionExists: false,
  },
  widget: {
    data: {
      agent_chat_image: null,
      attachment_anytime_enabled: false,
      banner_image_config: {
        alt_text: null,
        image: null,
        is_enabled: false,
        link: null,
      },
      csat_config: {
        is_enabled: true,
        negative_rating_routing_intent: undefined,
        no_resolve_option: '',
        one_to_five_labels: [],
        options_for_resolution_confirmation: [],
        question_for_additional_feedback: '',
        question_for_high_ratings: '',
        question_for_low_ratings: '',
        question_for_resolution_confirmation: '',
        rating_question: '',
        reasons_for_high_ratings: [],
        reasons_for_low_ratings: [],
        scale: 'one_to_two',
        should_request_additional_feedback: false,
        should_request_feedback_for_high_ratings: false,
        should_request_feedback_for_low_ratings: false,
        should_request_resolution_confirmation: false,
        should_show_submit_confirmation_message: false,
        should_use_rating_based_submit_message: false,
        show_csat_after_n_interactions: 1,
        show_on_header: false,
        show_when_agent_chat_ends: false,
        show_when_widget_minimizes: false,
        style: 'star',
        submit_confirmation_message: '',
        submit_message_for_high_ratings: '',
        submit_message_for_low_ratings: '',
        yes_resolve_option: '',
      },
      dictation_enabled: false,
      display_proactive_chat: false,
      display_proactive_chat_intents: false,
      enabled_web_call_brand_id: null,
      error_handling_config: {
        fallback_intent_workflow_id: null,
        fallback_message: '',
        is_enabled: false,
      },
      font: 'Plus Jakarta Sans',
      free_form_intent_detection_enabled: true,
      general_instruction_config: {
        general_context: '',
        glossary: '',
        is_enabled: false,
      },
      greeting_line: '',
      header_image: null,
      help_button_image: null,
      intro_config: {
        image: {
          enabled: false,
          value: '',
        },
        subtitle: {
          enabled: false,
          value: '',
        },
        title: {
          enabled: false,
          value: '',
        },
      },
      is_active: false,
      is_draft: true,
      is_modified: false,
      knowledge_configuration_prompt: '',
      launch_style: 'icon',
      loading_thinking_texts: [],
      manually_selected_intent_button_configuration: {},
      manually_selected_top_intents: [],
      new_streaming_ux_enabled: false,
      num_top_intents: 3,
      org_id: -1,
      placement: 'bottom_right',
      privacy_consent_config: {
        background_color: '',
        call_to_action_label: '',
        is_banner_dismissible: false,
        placement: 'top',
        privacy_policy: '',
        prompt_header: '',
        show_when_conversation_starts: false,
        style: 'call_to_action',
      },
      proactive_chat_greeting: null,
      quick_feedback_config: {
        feedback_prompt: '',
        hover_label_bad: '',
        hover_label_good: '',
        is_enabled: false,
        negative_routing_intent_workflow_id: '',
        positive_routing_intent_workflow_id: null,
        question_for_high_ratings: '',
        question_for_low_ratings: '',
        should_request_feedback_for_high_ratings: false,
        should_request_feedback_for_low_ratings: false,
        style: '',
      },
      restart_clears_handoff_transcript: false,
      sentiment_analysis_config: {
        is_enabled: false,
        negative_sentiment_routing_intent_workflow_id: null,
      },
      size: 'standard',
      tab_name: '',
      tag_selection_enabled: false,
      theme_color: '',
      tone_of_voice_config: {
        custom_prompt: null,
        enabled: false,
        predefined_tones: [],
        should_use_predefined_tones: false,
      },
      top_n_intents: null,
      use_help_button_image_as_icon: false,
      whitelisted_domains: [],
      widget_article_search_config: {
        enabled: false,
      },
    },
    error: null,
    loading: false,
  },
};

interface MultibrandParams {
  brandId?: string;
}

export const sortAvailableVoicesBySelectedId = (
  voices: AvailableVoice[],
  selectedVoiceId?: string | null,
): AvailableVoice[] => {
  return [...voices].sort((voice1, voice2) =>
    voice1.voice_id === selectedVoiceId
      ? -1
      : voice2.voice_id === selectedVoiceId
        ? 1
        : 0,
  );
};

export const filterVoicesByQuery = (
  voices: AvailableVoice[],
  query: string,
): AvailableVoice[] => {
  const normalizedQuery = query.trim().toLowerCase();
  return voices.filter(
    voice =>
      voice.name.toLowerCase().includes(normalizedQuery) ||
      voice.description.toLowerCase().includes(normalizedQuery),
  );
};

export const getInitialSolveConfiguration = createAsyncThunk(
  'solveConfig/getInitialSolveConfiguration',
  async ({ brandId }: MultibrandParams) => {
    const initialSolveLiteConfigResponse =
      await API.getInitialSolveConfiguration();
    if (initialSolveLiteConfigResponse.acknowledged) {
      if (brandId) {
        return API.getMultibrandWidgetConfiguration({ brandId });
      }
      return API.getWidgetConfiguration();
    }
  },
);

export const getWidgetConfiguration = createAsyncThunk(
  'solveConfig/getWidgetConfiguration',
  () => API.getWidgetConfiguration(),
);

export const getKnowledgeConfiguration = createAsyncThunk(
  'solveConfig/getKnowledgeConfiguration',
  () => API.getKnowledgeConfiguration(),
);

export const updateWidgetConfiguration = createAsyncThunk(
  'solveConfig/updateWidgetConfiguration',
  ({
    brandId,
    widgetConfiguration,
  }: { widgetConfiguration: WidgetConfiguration } & MultibrandParams) => {
    if (brandId) {
      return API.updateMultibrandWidgetConfiguration(widgetConfiguration, {
        brandId,
      });
    }
    return API.updateWidgetConfiguration(widgetConfiguration);
  },
);

export const getPlaybookConfiguration = createAsyncThunk(
  'solveConfig/getPlaybookConfiguration',
  () => API.getPlaybookConfiguration(),
);

export const updatePlaybookConfiguration = createAsyncThunk(
  'solveConfig/updatePlaybookConfiguration',
  async ({
    playbookConfiguration,
  }: {
    playbookConfiguration: PlaybookConfiguration;
  }) => {
    return API.updatePlaybookConfiguration(playbookConfiguration);
  },
);

export const updateEmailConfiguration = createAsyncThunk(
  'solveConfig/updateEmailConfiguration',
  async ({
    emailConfiguration,
  }: {
    emailConfiguration: EmailConfiguration;
  }) => {
    return API.updateEmailConfiguration(emailConfiguration);
  },
);

export const getSlackConfiguration = createAsyncThunk(
  'solveConfig/getSlackConfiguration',
  () => API.getSlackConfiguration(),
);

export const updateSlackConfiguration = createAsyncThunk(
  'solveConfig/updateSlackConfiguration',
  async ({
    slackConfiguration,
  }: {
    slackConfiguration: SlackConfiguration;
  }) => {
    return API.updateSlackConfiguration(slackConfiguration);
  },
);

export const getApiConfiguration = createAsyncThunk(
  'solveConfig/getApiConfiguration',
  () => API.getApiConfiguration(),
);

export const updateApiConfiguration = createAsyncThunk(
  'solveConfig/updateApiConfiguration',
  async ({ apiConfiguration }: { apiConfiguration: ApiConfiguration }) => {
    return API.updateApiConfiguration(apiConfiguration);
  },
);

export const updateKnowledgeConfiguration = createAsyncThunk(
  'solveConfig/updateKnowledgeConfiguration',
  (params: SolveKnowledgeSource) => {
    return API.updateKnowledgeConfiguration(params);
  },
);

export const activateWidget = createAsyncThunk(
  'solveConfig/activateWidget',
  ({ brandId }: MultibrandParams) => {
    if (brandId) {
      return API.activateMultibrandWidget({ brandId });
    }
    return API.activateWidget();
  },
);

export const deactivateWidget = createAsyncThunk(
  'solveConfig/deactivateWidget',
  ({ brandId }: MultibrandParams) => {
    if (brandId) {
      return API.deactivateMultibrandWidget({ brandId });
    }
    return API.deactivateWidget();
  },
);

export const publishConfiguration = createAsyncThunk(
  'solveConfig/publishConfiguration',
  ({ brandId }: MultibrandParams) => {
    if (brandId) {
      return API.publishMultibrandWidgetConfiguration({ brandId });
    }

    return API.publishWidgetConfiguration();
  },
);

export const discardConfiguration = createAsyncThunk(
  'solveConfig/discardConfiguration',
  async ({ brandId }: MultibrandParams, { dispatch }) => {
    if (brandId) {
      await API.discardMultibrandWidgetConfiguration({ brandId });
    } else {
      await API.discardWidgetConfiguration();
    }

    await dispatch(getInitialSolveConfiguration({ brandId })).unwrap();
  },
);

export const publishPlaybookConfiguration = createAsyncThunk(
  'solveConfig/publishPlaybookConfiguration',
  () => {
    return API.publishPlaybookConfiguration();
  },
);

export const discardPlaybookConfiguration = createAsyncThunk(
  'solveConfig/discardPlaybookConfiguration',
  async (_, { dispatch }) => {
    await API.discardPlaybookConfiguration();

    await dispatch(getPlaybookConfiguration()).unwrap();
  },
);

export const publishSlackConfiguration = createAsyncThunk(
  'solveConfig/publishSlackConfiguration',
  () => {
    return API.publishSlackConfiguration();
  },
);

export const discardSlackConfiguration = createAsyncThunk(
  'solveConfig/discardSlackConfiguration',
  async (_, { dispatch }) => {
    await API.discardSlackConfiguration();

    await dispatch(getSlackConfiguration()).unwrap();
  },
);

export const publishApiConfiguration = createAsyncThunk(
  'solveConfig/publishApiConfiguration',
  () => {
    return API.publishApiConfiguration();
  },
);

export const discardApiConfiguration = createAsyncThunk(
  'solveConfig/discardApiConfiguration',
  async (_, { dispatch }) => {
    await API.discardApiConfiguration();

    await dispatch(getApiConfiguration()).unwrap();
  },
);

export const publishVoiceConfiguration = createAsyncThunk(
  'solveConfig/publishVoiceConfiguration',
  ({ phoneNumber }: { phoneNumber: string }) => {
    return API.publishVoiceConfiguration({ phoneNumber });
  },
);

export const discardVoiceConfiguration = createAsyncThunk(
  'solveConfig/discardVoiceConfiguration',
  async ({ phoneNumber }: { phoneNumber: string }, { dispatch }) => {
    await API.discardVoiceConfiguration({ phoneNumber });

    await dispatch(
      getVoiceConfigurationByPhoneNumber({ phoneNumber }),
    ).unwrap();
  },
);

export const updateVoiceConfiguration = createAsyncThunk(
  'solveConfig/updateVoiceConfiguration',
  ({
    updatedConfigurations,
  }: {
    updatedConfigurations: VoiceConfiguration;
  }) => {
    return API.updateVoiceConfiguration({ updatedConfigurations });
  },
);

export const getVoiceConfigurationByPhoneNumber = createAsyncThunk(
  'solveConfig/getVoiceConfigurationByPhoneNumber',
  ({ phoneNumber }: { phoneNumber: string }) => {
    return API.getVoiceConfigurationByPhoneNumber({ phoneNumber });
  },
);

export const getVoiceConfigurationByBrand = createAsyncThunk(
  'solveConfig/getVoiceConfigurationByBrand',
  ({ brandId }: { brandId: string }) => {
    return API.getVoiceConfigurationByBrand({ brandId });
  },
);

export const publishVoiceConfigurationByBrand = createAsyncThunk(
  'solveConfig/publishVoiceConfigurationByBrand',
  ({ brandId }: { brandId: string }) => {
    return API.publishVoiceConfigurationByBrand({ brandId });
  },
);

export const discardVoiceConfigurationByBrand = createAsyncThunk(
  'solveConfig/discardVoiceConfigurationByBrand',
  async ({ brandId }: { brandId: string }, { dispatch }) => {
    await API.discardVoiceConfigurationByBrand({ brandId });

    await dispatch(getVoiceConfigurationByBrand({ brandId })).unwrap();
  },
);

export const getWebCallConfigurationByBrand = createAsyncThunk(
  'solveConfig/getWebCallConfigurationByBrand',
  ({ brandId }: { brandId: string }) => {
    return API.getWebCallConfigurationByBrand({ brandId });
  },
);

export const publishWebCallConfigurationByBrand = createAsyncThunk(
  'solveConfig/publishWebCallConfigurationByBrand',
  ({ brandId }: { brandId: string }) => {
    return API.publishWebCallConfigurationByBrand({ brandId });
  },
);

export const discardWebCallConfigurationByBrand = createAsyncThunk(
  'solveConfig/discardWebCallConfigurationByBrand',
  async ({ brandId }: { brandId: string }, { dispatch }) => {
    await API.discardWebCallConfigurationByBrand({ brandId });

    await dispatch(getWebCallConfigurationByBrand({ brandId })).unwrap();
  },
);

export const getBoughtPhoneNumbers = createAsyncThunk(
  'solveConfig/getBoughtPhoneNumbers',
  () => {
    return API.getBoughtPhoneNumbers();
  },
);

export const getAvailableVoices = createAsyncThunk(
  'solveConfig/getAvailableVoices',
  ({ language }: { language?: string }) => {
    return API.getAvailableVoices({ language });
  },
);

const solveConfigSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(
      getInitialSolveConfiguration.fulfilled,
      (state, { payload }) => {
        if (payload) {
          // since we used Promise.all in the action, payload is an array and the order matters
          const getWidgetConfigResponse = payload;
          // update the data
          state.isInitialized = true;
          state.liveVersionExists = getWidgetConfigResponse.live_version_exists;
          state.liveVersionIsActive =
            getWidgetConfigResponse.live_version_is_active;
          state.widget.data =
            getWidgetConfigResponse.solve_widget_configuration;
          state.isLoadingInitialConfig = false;
        }
      },
    );
    builder.addCase(
      getPlaybookConfiguration.fulfilled,
      (state, { payload }) => {
        state.isInitialized = true;
        state.playbook.data = payload.playbook_configuration;
        state.playbook.liveVersionExists = payload.has_live_version;
        state.playbook.loading = false;
        state.playbook.isLoadingInitialConfig = false;
        state.playbook.error = null;
      },
    );
    builder.addCase(
      updatePlaybookConfiguration.fulfilled,
      (state, { payload }) => {
        state.playbook.data = payload;
        state.playbook.loading = false;
        state.playbook.error = null;
      },
    );
    builder.addCase(updatePlaybookConfiguration.pending, state => {
      state.playbook.loading = true;
    });
    builder.addCase(updatePlaybookConfiguration.rejected, state => {
      state.playbook.loading = false;
    });

    // Slack configuration
    builder.addCase(getSlackConfiguration.fulfilled, (state, { payload }) => {
      state.isInitialized = true;
      state.isLoadingInitialConfig = false;
      state.slack.data = payload.slack_configuration;
      state.slack.liveVersionExists = payload.has_live_version;
      state.slack.loading = false;
      state.slack.error = null;
    });
    builder.addCase(getSlackConfiguration.pending, state => {
      state.slack.loading = true;
    });
    builder.addCase(getSlackConfiguration.rejected, state => {
      state.slack.loading = false;
    });
    builder.addCase(
      updateSlackConfiguration.fulfilled,
      (state, { payload }) => {
        state.slack.data = payload;
        state.slack.loading = false;
        state.slack.error = null;
      },
    );
    builder.addCase(updateSlackConfiguration.pending, state => {
      state.slack.loading = true;
    });
    builder.addCase(updateSlackConfiguration.rejected, state => {
      state.slack.loading = false;
    });

    // API configuration
    builder.addCase(getApiConfiguration.fulfilled, (state, { payload }) => {
      state.isInitialized = true;
      state.isLoadingInitialConfig = false;
      state.api.data = payload.solve_api_configuration;
      state.api.liveVersionExists = payload.has_live_version;
      state.api.loading = false;
      state.api.error = null;
    });
    builder.addCase(getApiConfiguration.pending, state => {
      state.api.loading = true;
    });
    builder.addCase(getApiConfiguration.rejected, state => {
      state.api.loading = false;
    });
    builder.addCase(updateApiConfiguration.fulfilled, (state, { payload }) => {
      state.api.data = payload;
      state.api.loading = false;
      state.api.error = null;
    });
    builder.addCase(updateApiConfiguration.pending, state => {
      state.api.loading = true;
    });
    builder.addCase(updateApiConfiguration.rejected, state => {
      state.api.loading = false;
    });
    builder.addCase(
      updateEmailConfiguration.fulfilled,
      (state, { payload }) => {
        state.email.data = payload;
        state.email.loading = false;
      },
    );
    builder.addCase(updateEmailConfiguration.pending, state => {
      state.email.loading = true;
    });
    builder.addCase(updateEmailConfiguration.rejected, state => {
      state.email.loading = false;
    });

    builder.addCase(getInitialSolveConfiguration.pending, state => {
      state.isLoadingInitialConfig = true;
    });
    builder.addCase(getWidgetConfiguration.fulfilled, (state, { payload }) => {
      state.liveVersionExists = payload.live_version_exists;
      state.widget.data = payload.solve_widget_configuration;
      state.widget.error = null;
    });
    builder.addCase(
      getKnowledgeConfiguration.fulfilled,
      (state, { payload }) => {
        state.knowledge.data = payload;
      },
    );
    builder.addCase(updateWidgetConfiguration.fulfilled, (state, action) => {
      // Redux state should be the source of truth for RTE/high frequency fields:
      const payloadWithoutRteFields = omit(
        [
          'greeting_line',
          'tab_name',
          'theme_color',
          'proactive_chat_greeting',
          'csat_config',
          'privacy_consent_config',
          'enabled_web_call_brand_id',
          // contains an RTE field (fallback_message); Redux is source of truth
          'error_handling_config',

          // High-frequency fields, needed to avoid UI glitching:
          'placement',
          'size',
        ],
        action.payload,
      );

      Object.assign(state.widget.data, payloadWithoutRteFields);

      state.widget.error = null;
      state.widget.loading = false;
    });
    builder.addCase(updateWidgetConfiguration.pending, state => {
      state.widget.loading = true;
    });
    builder.addCase(updateWidgetConfiguration.rejected, state => {
      state.widget.loading = false;
    });

    // Voice configuration
    builder.addCase(getVoiceConfigurationByPhoneNumber.pending, state => {
      state.uiLoadingState.isSwitchingConfig = true;
    });
    builder.addCase(
      getVoiceConfigurationByPhoneNumber.fulfilled,
      (state, { payload }) => {
        if (payload) {
          const sortedVoices = sortAvailableVoicesBySelectedId(
            payload.available_voices,
            payload.solve_voice_configuration.agent_voice_id,
          );

          // set the values in redux
          state.isInitialized = true;
          state.isLoadingInitialConfig = false;
          state.voice.data = payload.solve_voice_configuration;
          state.voice.available_voices = sortedVoices;
          state.voice.selectedPhoneNumber =
            payload.solve_voice_configuration.phone_number || null;

          state.liveVersionExists = payload.live_version_exists;
          state.voice.error = null;
          state.uiLoadingState.isSwitchingConfig = false;
        }
      },
    );
    builder.addCase(getVoiceConfigurationByPhoneNumber.rejected, state => {
      state.uiLoadingState.isSwitchingConfig = true;
    });

    builder.addCase(getVoiceConfigurationByBrand.pending, state => {
      state.uiLoadingState.isSwitchingConfig = true;
    });
    builder.addCase(
      getVoiceConfigurationByBrand.fulfilled,
      (state, { payload }) => {
        if (payload) {
          const sortedVoices = sortAvailableVoicesBySelectedId(
            payload.available_voices,
            payload.solve_voice_configuration.agent_voice_id,
          );

          // set the values in redux
          state.isInitialized = true;
          state.isLoadingInitialConfig = false;
          state.voice.data = payload.solve_voice_configuration;
          state.voice.available_voices = sortedVoices;
          state.voice.selectedPhoneNumber =
            payload.solve_voice_configuration.phone_number || null;

          state.liveVersionExists = payload.live_version_exists;
          state.voice.error = null;
          state.uiLoadingState.isSwitchingConfig = false;
        }
      },
    );
    builder.addCase(getVoiceConfigurationByBrand.rejected, state => {
      state.uiLoadingState.isSwitchingConfig = true;
    });

    builder.addCase(getAvailableVoices.fulfilled, (state, { payload }) => {
      state.voice.available_voices = payload.available_voices;
    });
    builder.addCase(getAvailableVoices.rejected, state => {
      state.voice.available_voices = [];
    });

    builder.addCase(updateVoiceConfiguration.fulfilled, state => {
      state.voice.data.is_modified = true;
      state.voice.error = null;
      state.voice.loading = false;
    });
    builder.addCase(updateVoiceConfiguration.pending, (state, action) => {
      state.voice.loading = true;
      state.voice.data = action.meta.arg.updatedConfigurations;
    });
    builder.addCase(updateVoiceConfiguration.rejected, state => {
      state.voice.loading = false;
    });

    builder.addCase(getBoughtPhoneNumbers.fulfilled, (state, action) => {
      state.voice.bought_phone_numbers = action.payload.phone_number_configs;
      state.voice.is_customer_twilio_account =
        action.payload.is_customer_twilio_account;
      state.isInitialized = true;
      state.isLoadingInitialConfig = false;
    });

    builder.addCase(activateWidget.fulfilled, state => {
      state.liveVersionIsActive = true;
      state.widget.error = null;
    });
    builder.addCase(deactivateWidget.fulfilled, state => {
      state.liveVersionIsActive = false;
      state.widget.error = null;
    });
    builder.addCase(publishConfiguration.fulfilled, state => {
      state.liveVersionExists = true;
      state.widget.data.is_modified = false;
      state.knowledge.data.is_modified = false;
      state.isPublishing = false;
    });
    builder.addCase(publishConfiguration.pending, state => {
      state.isPublishing = true;
    });
    builder.addCase(publishConfiguration.rejected, state => {
      state.isPublishing = false;
    });
    builder.addCase(discardConfiguration.pending, state => {
      state.isDiscarding = true;
      // Blocks the autosave debounce from PUTting stale form values back over the reverted draft.
      state.isLoadingInitialConfig = true;
    });
    builder.addCase(discardConfiguration.fulfilled, state => {
      state.isDiscarding = false;
    });
    builder.addCase(discardConfiguration.rejected, state => {
      state.isDiscarding = false;
      state.isLoadingInitialConfig = false;
    });
    builder.addCase(publishPlaybookConfiguration.fulfilled, state => {
      state.playbook.liveVersionExists = true;
      state.playbook.data.is_modified = false;
      state.isPublishing = false;
    });
    builder.addCase(publishPlaybookConfiguration.pending, state => {
      state.isPublishing = true;
    });
    builder.addCase(publishPlaybookConfiguration.rejected, state => {
      state.isPublishing = false;
    });
    builder.addCase(discardPlaybookConfiguration.pending, state => {
      state.isDiscarding = true;
      state.playbook.isLoadingInitialConfig = true;
    });
    builder.addCase(discardPlaybookConfiguration.fulfilled, state => {
      state.isDiscarding = false;
    });
    builder.addCase(discardPlaybookConfiguration.rejected, state => {
      state.isDiscarding = false;
      state.playbook.isLoadingInitialConfig = false;
    });
    builder.addCase(publishVoiceConfiguration.fulfilled, state => {
      state.liveVersionExists = true;
      state.widget.data.is_modified = false;
      state.voice.data.is_modified = false;
      state.isPublishing = false;
    });
    builder.addCase(publishVoiceConfiguration.pending, state => {
      state.isPublishing = true;
    });
    builder.addCase(publishVoiceConfiguration.rejected, state => {
      state.isPublishing = false;
    });
    builder.addCase(discardVoiceConfiguration.pending, state => {
      state.isDiscarding = true;
      state.isLoadingInitialConfig = true;
    });
    builder.addCase(discardVoiceConfiguration.fulfilled, state => {
      state.isDiscarding = false;
    });
    builder.addCase(discardVoiceConfiguration.rejected, state => {
      state.isDiscarding = false;
      state.isLoadingInitialConfig = false;
    });
    builder.addCase(publishVoiceConfigurationByBrand.fulfilled, state => {
      state.liveVersionExists = true;
      state.widget.data.is_modified = false;
      state.voice.data.is_modified = false;
      state.isPublishing = false;
    });
    builder.addCase(publishVoiceConfigurationByBrand.pending, state => {
      state.isPublishing = true;
    });
    builder.addCase(publishVoiceConfigurationByBrand.rejected, state => {
      state.isPublishing = false;
    });
    builder.addCase(discardVoiceConfigurationByBrand.pending, state => {
      state.isDiscarding = true;
      state.isLoadingInitialConfig = true;
    });
    builder.addCase(discardVoiceConfigurationByBrand.fulfilled, state => {
      state.isDiscarding = false;
    });
    builder.addCase(discardVoiceConfigurationByBrand.rejected, state => {
      state.isDiscarding = false;
      state.isLoadingInitialConfig = false;
    });

    // Web call configuration
    builder.addCase(getWebCallConfigurationByBrand.pending, state => {
      state.webCall.isLoading = true;
      state.uiLoadingState.isSwitchingConfig = true;
    });
    builder.addCase(
      getWebCallConfigurationByBrand.fulfilled,
      (state, { payload }) => {
        state.isInitialized = true;
        state.isLoadingInitialConfig = false;
        state.webCall.isLoading = false;
        state.uiLoadingState.isSwitchingConfig = false;

        if (payload) {
          state.webCall.data = payload.web_call_configuration;
          state.webCall.isModified = Boolean(
            payload.web_call_configuration.is_modified,
          );
          state.webCall.liveVersionExists = Boolean(
            payload.live_version_exists,
          );
        }
      },
    );
    builder.addCase(getWebCallConfigurationByBrand.rejected, state => {
      state.isInitialized = true;
      state.isLoadingInitialConfig = false;
      state.webCall.isLoading = false;
      state.uiLoadingState.isSwitchingConfig = false;
    });
    builder.addCase(publishWebCallConfigurationByBrand.pending, state => {
      state.isPublishing = true;
    });
    builder.addCase(publishWebCallConfigurationByBrand.fulfilled, state => {
      state.webCall.isModified = false;
      state.webCall.liveVersionExists = true;
      state.isPublishing = false;
    });
    builder.addCase(publishWebCallConfigurationByBrand.rejected, state => {
      state.isPublishing = false;
    });
    builder.addCase(discardWebCallConfigurationByBrand.pending, state => {
      state.isDiscarding = true;
      state.isLoadingInitialConfig = true;
    });
    builder.addCase(discardWebCallConfigurationByBrand.fulfilled, state => {
      state.isDiscarding = false;
    });
    builder.addCase(discardWebCallConfigurationByBrand.rejected, state => {
      state.isDiscarding = false;
      state.isLoadingInitialConfig = false;
    });

    builder.addCase(publishSlackConfiguration.fulfilled, state => {
      state.slack.liveVersionExists = true;
      state.slack.data.is_modified = false;
      state.isPublishing = false;
    });
    builder.addCase(publishSlackConfiguration.pending, state => {
      state.isPublishing = true;
    });
    builder.addCase(publishSlackConfiguration.rejected, state => {
      state.isPublishing = false;
    });
    builder.addCase(discardSlackConfiguration.pending, state => {
      state.isDiscarding = true;
      state.isLoadingInitialConfig = true;
    });
    builder.addCase(discardSlackConfiguration.fulfilled, state => {
      state.isDiscarding = false;
    });
    builder.addCase(discardSlackConfiguration.rejected, state => {
      state.isDiscarding = false;
      state.isLoadingInitialConfig = false;
    });
    builder.addCase(publishApiConfiguration.fulfilled, state => {
      state.api.liveVersionExists = true;
      state.api.data.is_modified = false;
      state.isPublishing = false;
    });
    builder.addCase(publishApiConfiguration.pending, state => {
      state.isPublishing = true;
    });
    builder.addCase(publishApiConfiguration.rejected, state => {
      state.isPublishing = false;
    });
    builder.addCase(discardApiConfiguration.pending, state => {
      state.isDiscarding = true;
      state.isLoadingInitialConfig = true;
    });
    builder.addCase(discardApiConfiguration.fulfilled, state => {
      state.isDiscarding = false;
    });
    builder.addCase(discardApiConfiguration.rejected, state => {
      state.isDiscarding = false;
      state.isLoadingInitialConfig = false;
    });
    // update knowledge config
    builder.addCase(updateKnowledgeConfiguration.rejected, state => {
      state.knowledge.loading = false;
    });
    builder.addCase(updateKnowledgeConfiguration.pending, state => {
      state.knowledge.loading = true;
    });
    builder.addCase(
      updateKnowledgeConfiguration.fulfilled,
      (state, { payload }) => {
        state.knowledge.data = payload;
        state.knowledge.error = null;
        state.knowledge.loading = false;
      },
    );
  },
  initialState,
  name: 'solveConfig',
  reducers: {
    addBoughtPhoneNumber(
      state: SolveConfigState,
      action: PayloadAction<{
        phoneNumber: string;
        provider: PhoneNumberProvider;
      }>,
    ) {
      state.voice.bought_phone_numbers = [
        ...state.voice.bought_phone_numbers,
        {
          language: 'English',
          phone_number: action.payload.phoneNumber,
          provider: action.payload.provider,
        },
      ];
    },
    addBoughtPhoneNumbers(
      state: SolveConfigState,
      action: PayloadAction<{
        phoneNumbers: string[];
        provider: PhoneNumberProvider;
      }>,
    ) {
      state.voice.bought_phone_numbers = [
        ...state.voice.bought_phone_numbers,
        ...action.payload.phoneNumbers.map(phoneNumber => ({
          language: 'English',
          phone_number: phoneNumber,
          provider: action.payload.provider,
        })),
      ];
    },
    hideWarningModal(state: SolveConfigState) {
      state.validationState.isWarningModalVisible = false;
    },
    removeBoughtPhoneNumber(
      state: SolveConfigState,
      action: PayloadAction<string>,
    ) {
      state.voice.bought_phone_numbers =
        state.voice.bought_phone_numbers.filter(
          numberConfig => numberConfig.phone_number !== action.payload,
        );
    },
    setApiGeneralInstructionConfig(
      state: SolveConfigState,
      action: PayloadAction<GeneralInstructionConfig>,
    ) {
      state.api.data.general_instruction_config = action.payload;
    },
    setAssistGeneralInstructionConfig(
      state: SolveConfigState,
      action: PayloadAction<GeneralInstructionConfig>,
    ) {
      state.playbook.data.general_instruction_config = action.payload;
    },
    setAssistGeneralInstructionConfigGeneratedReply(
      state: SolveConfigState,
      action: PayloadAction<GeneralInstructionConfig>,
    ) {
      state.playbook.data.reply_generation_general_instruction_config =
        action.payload;
    },
    setAttachmentAnytimeEnabled(
      state: SolveConfigState,
      action: PayloadAction<boolean>,
    ) {
      state.widget.data.attachment_anytime_enabled = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setBannerImageConfig(
      state: SolveConfigState,
      action: PayloadAction<BannerImageConfig>,
    ) {
      state.widget.data.banner_image_config = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isHighFrequencyFieldsLoading: true,
      };
    },
    setBrandId(state: SolveConfigState, action: PayloadAction<string>) {
      state.brandId = action.payload;
      // Guard saves synchronously: getInitialSolveConfiguration.pending fires too
      // late, letting a debounced save write the previous brand's data (SOL-9857).
      state.isLoadingInitialConfig = true;
    },
    setCsatConfigByKey<
      T extends keyof SolveConfigState['widget']['data']['csat_config'],
    >(
      state: SolveConfigState,
      action: PayloadAction<{
        isGenericField?: boolean;
        key: T;
        value: SolveConfigState['widget']['data']['csat_config'][T];
      }>,
    ) {
      const { isGenericField, key, value } = action.payload;
      // if we're changing scale, should also update the style to the default style under
      // that scale
      if (key === 'scale' && value !== state.widget.data.csat_config.scale) {
        state.widget.data.csat_config.style =
          value === 'one_to_five' ? 'star' : 'thumbs';
      }

      state.widget.data.csat_config[key] = value;

      // determine what kind of field is being loading
      if (isGenericField !== undefined) {
        state.uiLoadingState = {
          ...state.uiLoadingState,
          isGenericFieldsLoading: true,
        };
      } else {
        state.uiLoadingState = {
          ...state.uiLoadingState,
          isHighFrequencyFieldsLoading: true,
        };
      }
    },
    setDictationEnabled(
      state: SolveConfigState,
      action: PayloadAction<boolean>,
    ) {
      state.widget.data.dictation_enabled = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setEmailConfigurationFields(
      state: SolveConfigState,
      action: PayloadAction<{
        field_values: Record<string, unknown> | null;
        isFormValid?: boolean;
        isModified?: boolean;
        modified_date: string | null;
      }>,
    ) {
      state.emailConfigurationFields = {
        ...state.emailConfigurationFields,
        ...action.payload,
      };
    },
    setEmailGeneralInstructionConfig(
      state: SolveConfigState,
      action: PayloadAction<GeneralInstructionConfig>,
    ) {
      state.email.data.general_instruction_config = action.payload;
    },
    setEmailKnowledgeConfigurationPrompt(
      state: SolveConfigState,
      action: PayloadAction<string>,
    ) {
      state.emailKnowledgeConfigurationPrompt = action.payload;
    },
    setEmailSettingsFormValues(
      state: SolveConfigState,
      action: PayloadAction<{
        admin_id: string | null;
        email_response_width: EmailResponseWidth;
        from_email_mapping: Array<{ key: string; value: string }>;
        front_private_emails: string[];
        interactive_email_browser_tab_name: string | null;
        modified_date: string | null;
        org_wide_email_name: string | null;
        to_email_mapping: Array<{ key: string; value: string }>;
      }>,
    ) {
      state.emailSettingsFormValues = action.payload;
    },
    setEmailToneOfVoiceConfig(
      state: SolveConfigState,
      action: PayloadAction<ToneOfVoiceConfig>,
    ) {
      state.emailToneOfVoiceConfig = action.payload;
    },
    setErrorHandlingConfigByKey<
      T extends
        keyof SolveConfigState['widget']['data']['error_handling_config'],
    >(
      state: SolveConfigState,
      action: PayloadAction<{
        key: T;
        value: SolveConfigState['widget']['data']['error_handling_config'][T];
      }>,
    ) {
      const { key, value } = action.payload;

      state.widget.data.error_handling_config[key] = value;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setExcludePrivateArticles(
      state: SolveConfigState,
      action: PayloadAction<boolean>,
    ) {
      if (!state.knowledge.data.selected_knowledge_source) {
        return;
      }

      state.knowledge.data.selected_knowledge_source.exclude_private_articles =
        action.payload;

      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setFont(
      state: SolveConfigState,
      action: PayloadAction<WidgetConfiguration['font']>,
    ) {
      state.widget.data.font = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setFreeFormIntentDetectionEnabled(
      state: SolveConfigState,
      action: PayloadAction<boolean>,
    ) {
      state.widget.data.free_form_intent_detection_enabled = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setGeneralConfig(
      state: SolveConfigState,
      action: PayloadAction<PlaybookConfiguration['general_config']>,
    ) {
      state.playbook.data.general_config = action.payload;
    },
    setGeneralInstructionConfig(
      state: SolveConfigState,
      action: PayloadAction<GeneralInstructionConfig>,
    ) {
      state.widget.data.general_instruction_config = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setGreetingLine(state: SolveConfigState, action: PayloadAction<string>) {
      state.widget.data.greeting_line = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isHighFrequencyFieldsLoading: true,
      };
    },
    setIntentOptions(state: SolveConfigState, action: PayloadAction<string>) {
      if (action.payload === 'no_display') {
        state.widget.data.manually_selected_top_intents = [];
        state.widget.data.num_top_intents = 0;
      }
      if (action.payload === 'auto') {
        state.widget.data.manually_selected_top_intents = [];
        state.widget.data.num_top_intents = 3;
      }
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setIntroImageEnabled(
      state: SolveConfigState,
      action: PayloadAction<
        WidgetConfiguration['intro_config']['image']['enabled']
      >,
    ) {
      state.widget.data.intro_config.image.enabled = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setIntroSubtitleEnabled(
      state: SolveConfigState,
      action: PayloadAction<
        WidgetConfiguration['intro_config']['subtitle']['enabled']
      >,
    ) {
      state.widget.data.intro_config.subtitle.enabled = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setIntroSubtitleValue(
      state: SolveConfigState,
      action: PayloadAction<
        WidgetConfiguration['intro_config']['subtitle']['value']
      >,
    ) {
      state.widget.data.intro_config.subtitle.value = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isHighFrequencyFieldsLoading: true,
      };
    },
    setIntroTitleEnabled(
      state: SolveConfigState,
      action: PayloadAction<
        WidgetConfiguration['intro_config']['title']['enabled']
      >,
    ) {
      state.widget.data.intro_config.title.enabled = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setIntroTitleValue(
      state: SolveConfigState,
      action: PayloadAction<
        WidgetConfiguration['intro_config']['title']['value']
      >,
    ) {
      state.widget.data.intro_config.title.value = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isHighFrequencyFieldsLoading: true,
      };
    },
    setIsGenericFieldsLoading(
      state: SolveConfigState,
      action: PayloadAction<boolean>,
    ) {
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: action.payload,
      };
    },
    setIsHighFrequencyFieldsLoading(
      state: SolveConfigState,
      action: PayloadAction<boolean>,
    ) {
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isHighFrequencyFieldsLoading: action.payload,
      };
    },
    setIsSwitchingConfig(
      state: SolveConfigState,
      action: PayloadAction<boolean>,
    ) {
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isSwitchingConfig: action.payload,
      };
    },
    setKnowledgeConfigurationPrompt(
      state: SolveConfigState,
      action: PayloadAction<{
        shouldShowLoadingsState?: boolean | undefined;
        shouldUseLongerNoteLength: boolean;
        value: string;
      }>,
    ) {
      state.widget.data.knowledge_configuration_prompt = action.payload.value;

      state.isLongerNoteEnabled = action.payload.shouldUseLongerNoteLength;
      if (action.payload.shouldShowLoadingsState !== false) {
        state.uiLoadingState = {
          ...state.uiLoadingState,
          isHighFrequencyFieldsLoading: true,
        };
      }
    },
    setKnowledgeConfigurationPromptPlaybook(
      state: SolveConfigState,
      action: PayloadAction<{
        shouldShowLoadingsState?: boolean | undefined;
        shouldUseLongerNoteLength: boolean;
        value: string;
      }>,
    ) {
      state.playbook.data.knowledge_configuration_prompt = action.payload.value;

      state.isLongerNoteEnabled = action.payload.shouldUseLongerNoteLength;
      if (action.payload.shouldShowLoadingsState !== false) {
        state.uiLoadingState = {
          ...state.uiLoadingState,
          isHighFrequencyFieldsLoading: true,
        };
      }
    },
    setLaunchStyle(
      state: SolveConfigState,
      action: PayloadAction<WidgetConfiguration['launch_style']>,
    ) {
      state.widget.data.launch_style = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setLiveVersionIsActive(
      state: SolveConfigState,
      action: PayloadAction<boolean>,
    ) {
      state.liveVersionIsActive = action.payload;
    },
    setManuallySelectedIntentButtonConfiguration(
      state: SolveConfigState,
      action: PayloadAction<
        WidgetConfiguration['manually_selected_intent_button_configuration']
      >,
    ) {
      state.widget.data.manually_selected_intent_button_configuration =
        action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setManuallySelectedTopIntents(
      state: SolveConfigState,
      action: PayloadAction<string[]>,
    ) {
      state.widget.data.manually_selected_top_intents = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setNumTopIntents(state: SolveConfigState, action: PayloadAction<number>) {
      state.widget.data.num_top_intents = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setPlacement(
      state: SolveConfigState,
      action: PayloadAction<WidgetConfiguration['placement']>,
    ) {
      state.widget.data.placement = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setPrivacyConsentConfigurationByKey<
      T extends
        keyof SolveConfigState['widget']['data']['privacy_consent_config'],
    >(
      state: SolveConfigState,
      action: PayloadAction<{
        isGenericField?: boolean;
        key: T;
        value: SolveConfigState['widget']['data']['privacy_consent_config'][T];
      }>,
    ) {
      const { isGenericField, key, value } = action.payload;
      state.widget.data.privacy_consent_config[key] = value;

      // determine what kind of field is being loading
      if (isGenericField !== undefined) {
        state.uiLoadingState = {
          ...state.uiLoadingState,
          isGenericFieldsLoading: true,
        };
      } else {
        state.uiLoadingState = {
          ...state.uiLoadingState,
          isHighFrequencyFieldsLoading: true,
        };
      }
    },
    setProactiveChatGreeting(
      state: SolveConfigState,
      action: PayloadAction<string>,
    ) {
      state.widget.data.proactive_chat_greeting = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isHighFrequencyFieldsLoading: true,
      };
    },
    setQuickFeedbackConfigByKey<
      T extends
        keyof SolveConfigState['widget']['data']['quick_feedback_config'],
    >(
      state: SolveConfigState,
      action: PayloadAction<{
        isGenericField?: boolean;
        isPlaybook?: boolean;
        key: T;
        value: SolveConfigState['widget']['data']['quick_feedback_config'][T];
      }>,
    ) {
      const { isGenericField, isPlaybook, key, value } = action.payload;

      if (isPlaybook) {
        set(state.playbook.data.quick_feedback_config, key, value);
      } else {
        state.widget.data.quick_feedback_config[key] = value;
      }
      // determine what kind of field is being loading
      if (isGenericField !== undefined) {
        state.uiLoadingState = {
          ...state.uiLoadingState,
          isGenericFieldsLoading: true,
        };
      } else {
        state.uiLoadingState = {
          ...state.uiLoadingState,
          isHighFrequencyFieldsLoading: true,
        };
      }
    },
    setReplyGenerationPromptPlaybook(
      state: SolveConfigState,
      action: PayloadAction<{
        shouldShowLoadingsState?: boolean | undefined;
        shouldUseLongerNoteLength: boolean;
        value: string;
      }>,
    ) {
      state.playbook.data.knowledge_configuration_prompt = action.payload.value;

      state.isLongerNoteEnabled = action.payload.shouldUseLongerNoteLength;
      if (action.payload.shouldShowLoadingsState !== false) {
        state.uiLoadingState = {
          ...state.uiLoadingState,
          isHighFrequencyFieldsLoading: true,
        };
      }
    },
    setRestartClearsHandoffTranscript(
      state: SolveConfigState,
      action: PayloadAction<boolean>,
    ) {
      state.widget.data.restart_clears_handoff_transcript = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setSelectedPhoneNumber(
      state: SolveConfigState,
      action: PayloadAction<string | null>,
    ) {
      state.voice.selectedPhoneNumber = action.payload;
    },
    setSentimentConfigByKey<
      T extends
        keyof SolveConfigState['widget']['data']['sentiment_analysis_config'],
    >(
      state: SolveConfigState,
      action: PayloadAction<{
        key: T;
        value: SolveConfigState['widget']['data']['sentiment_analysis_config'][T];
      }>,
    ) {
      const { key, value } = action.payload;

      state.widget.data.sentiment_analysis_config[key] = value;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setSize(
      state: SolveConfigState,
      action: PayloadAction<WidgetConfiguration['size']>,
    ) {
      state.widget.data.size = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setSlackGeneralInstructionConfig(
      state: SolveConfigState,
      action: PayloadAction<GeneralInstructionConfig>,
    ) {
      state.slack.data.general_instruction_config = action.payload;
    },
    setSolveConfigInitialized(state: SolveConfigState) {
      state.isInitialized = true;
    },
    setStartUploadingImage(state: SolveConfigState) {
      state.widget.loading = true;
      setIsGenericFieldsLoading(true);
    },
    setTabName(state: SolveConfigState, action: PayloadAction<string>) {
      state.widget.data.tab_name = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isHighFrequencyFieldsLoading: true,
      };
    },
    setTagSelectionEnabled(
      state: SolveConfigState,
      action: PayloadAction<boolean>,
    ) {
      state.widget.data.tag_selection_enabled = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setThemeColor(state: SolveConfigState, action: PayloadAction<string>) {
      state.widget.data.theme_color = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isHighFrequencyFieldsLoading: true,
      };
    },
    setToneOfVoiceConfig(
      state: SolveConfigState,
      action: PayloadAction<ToneOfVoiceConfig>,
    ) {
      state.widget.data.tone_of_voice_config = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isHighFrequencyFieldsLoading: true,
      };
    },
    setToneOfVoiceConfigApi(
      state: SolveConfigState,
      action: PayloadAction<ToneOfVoiceConfig>,
    ) {
      state.api.data.tone_of_voice_config = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isHighFrequencyFieldsLoading: true,
      };
    },
    setToneOfVoiceConfigPlaybook(
      state: SolveConfigState,
      action: PayloadAction<ToneOfVoiceConfig>,
    ) {
      state.playbook.data.tone_of_voice_config = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isHighFrequencyFieldsLoading: true,
      };
    },
    setToneOfVoiceConfigPlaybookGeneratedReply(
      state: SolveConfigState,
      action: PayloadAction<ToneOfVoiceConfig>,
    ) {
      state.playbook.data.reply_generation_tone_of_voice_config =
        action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isHighFrequencyFieldsLoading: true,
      };
    },
    setToneOfVoiceConfigSlack(
      state: SolveConfigState,
      action: PayloadAction<ToneOfVoiceConfig>,
    ) {
      state.slack.data.tone_of_voice_config = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isHighFrequencyFieldsLoading: true,
      };
    },
    setVoiceConfiguration(
      state: SolveConfigState,
      action: PayloadAction<VoiceConfiguration>,
    ) {
      state.voice.data = action.payload;
    },
    setVoiceCsatConfigByKey<
      T extends keyof SolveConfigState['voice']['data']['csat_config'],
    >(
      state: SolveConfigState,
      action: PayloadAction<{
        isGenericField?: boolean;
        key: T;
        value: SolveConfigState['voice']['data']['csat_config'][T];
      }>,
    ) {
      const { isGenericField, key, value } = action.payload;
      // if we're changing scale, should also update the style to the default style under
      // that scale
      if (key === 'scale' && value !== state.widget.data.csat_config.scale) {
        state.widget.data.csat_config.style =
          value === 'one_to_five' ? 'star' : 'thumbs';
      }

      state.voice.data.csat_config[key] = value;

      // determine what kind of field is being loading
      if (isGenericField !== undefined) {
        state.uiLoadingState = {
          ...state.uiLoadingState,
          isGenericFieldsLoading: true,
        };
      } else {
        state.uiLoadingState = {
          ...state.uiLoadingState,
          isHighFrequencyFieldsLoading: true,
        };
      }
    },
    setVoiceDataByKey<T extends keyof SolveConfigState['voice']['data']>(
      state: SolveConfigState,
      action: PayloadAction<{
        key: T;
        value: SolveConfigState['voice']['data'][T];
      }>,
    ) {
      const { key, value } = action.payload;
      state.voice.data[key] = value;

      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setWebCallData(
      state: SolveConfigState,
      action: PayloadAction<WebCallConfigData>,
    ) {
      state.webCall.data = action.payload;
      state.webCall.isModified = Boolean(action.payload.is_modified);
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setWebCallDataByKey(
      state: SolveConfigState,
      action: PayloadAction<{ key: string; value: unknown }>,
    ) {
      if (state.webCall.data) {
        (state.webCall.data as unknown as Record<string, unknown>)[
          action.payload.key
        ] = action.payload.value;
        state.webCall.isModified = true;
      }

      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setWidgetArticleSearchEnabled(
      state: SolveConfigState,
      action: PayloadAction<boolean>,
    ) {
      state.widget.data.widget_article_search_config.enabled = action.payload;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    setWidgetDataByKey<T extends keyof SolveConfigState['widget']['data']>(
      state: SolveConfigState,
      action: PayloadAction<{
        key: T;
        value: SolveConfigState['widget']['data'][T];
      }>,
    ) {
      const { key, value } = action.payload;
      state.widget.data[key] = value;
      state.uiLoadingState = {
        ...state.uiLoadingState,
        isGenericFieldsLoading: true,
      };
    },
    showWarningModal(state: SolveConfigState) {
      state.validationState.isWarningModalVisible = true;
    },
    swapManuallySelectedTopIntents(
      state: SolveConfigState,
      action: PayloadAction<{ dragIndex: number; hoverIndex: number }>,
    ) {
      const { dragIndex, hoverIndex } = action.payload;

      // Swap elements by index:
      [
        state.widget.data.manually_selected_top_intents[dragIndex],
        state.widget.data.manually_selected_top_intents[hoverIndex],
      ] = [
        state.widget.data.manually_selected_top_intents[hoverIndex],
        state.widget.data.manually_selected_top_intents[dragIndex],
      ];
    },
  },
});

export const selectIsSolveConfigInitialized = (state: RootState): boolean =>
  state.solveConfig.isInitialized;

export const selectWidgetConfiguration = (
  state: RootState,
): WidgetConfiguration => state.solveConfig.widget.data;

export const selectWidgetAvatarImage = (state: RootState): string =>
  state.solveConfig.widget.data.agent_chat_image
    ? state.solveConfig.widget.data.agent_chat_image
    : agentChatImageDefaultIcon;

export const selectVoiceConfiguration = (
  state: RootState,
): VoiceConfiguration => state.solveConfig.voice.data;

export const selectBoughtNumbers = (state: RootState): PhoneNumberConfig[] =>
  state.solveConfig.voice.bought_phone_numbers;

export const selectIsCustomerTwilioAccount = (state: RootState): boolean =>
  state.solveConfig.voice.is_customer_twilio_account;

export const selectSelectedPhoneNumber = (state: RootState): string | null =>
  state.solveConfig.voice.selectedPhoneNumber;

export const selectAgentVoiceId = (state: RootState): string =>
  state.solveConfig.voice.data.agent_voice_id;

export const selectAvailableVoices = (state: RootState): AvailableVoice[] =>
  state.solveConfig.voice.available_voices;

export const selectIsSwitchingConfig = (state: RootState): boolean =>
  state.solveConfig.uiLoadingState.isSwitchingConfig;

export const selectKnowledgeConfiguration = (
  state: RootState,
): KnowledgeConfiguration => state.solveConfig.knowledge.data;

export const selectSlackConfiguration = (
  state: RootState,
): SlackConfiguration => state.solveConfig.slack.data;

export const selectApiConfiguration = (state: RootState): ApiConfiguration =>
  state.solveConfig.api.data;

export const selectEmailConfiguration = (
  state: RootState,
): EmailConfiguration => ({
  ...state.solveConfig.email.data,
  tone_of_voice_config: state.solveConfig.emailToneOfVoiceConfig,
});

export const selectUiLoadingState = (
  state: RootState,
): {
  isGenericFieldsLoading: boolean;
  isHighFrequencyFieldsLoading: boolean;
} => state.solveConfig.uiLoadingState;

export const selectIsSaving = (state: RootState): boolean => {
  return (
    state.solveConfig.uiLoadingState.isGenericFieldsLoading ||
    state.solveConfig.uiLoadingState.isHighFrequencyFieldsLoading ||
    state.solveConfig.knowledge.loading ||
    state.solveConfig.widget.loading ||
    state.solveConfig.voice.loading ||
    state.solveConfig.isPublishing ||
    state.solveConfig.playbook.loading ||
    state.solveConfig.slack.loading ||
    state.solveConfig.api.loading
  );
};

const selectIsConfigurationModified = (state: RootState): boolean => {
  return (
    state.solveConfig.knowledge.data.is_modified ||
    state.solveConfig.widget.data.is_modified
  );
};

export const selectIsActive = (state: RootState): boolean => {
  return state.solveConfig.liveVersionIsActive;
};

export const selectLiveVersionExists = (state: RootState): boolean => {
  return state.solveConfig.liveVersionExists;
};

export const selectLiveVersionExistsPlaybook = (state: RootState): boolean =>
  state.solveConfig.playbook.liveVersionExists;

export const selectLiveVersionExistsApi = (state: RootState): boolean =>
  state.solveConfig.api.liveVersionExists;

export const selectLiveVersionExistsSlack = (state: RootState): boolean =>
  state.solveConfig.slack.liveVersionExists;

export const selectCanUpdatePublish = createSelector(
  (state: RootState) => selectIsConfigurationModified(state),
  (state: RootState) => selectLiveVersionExists(state),
  (isConfigurationModified, liveVersionExists) =>
    isConfigurationModified && liveVersionExists,
);

export const selectIsWidgetConfigModified = (state: RootState): boolean =>
  state.solveConfig.widget.data.is_modified;

export const selectIsDiscarding = (state: RootState): boolean =>
  state.solveConfig.isDiscarding;

export const selectIsVoiceConfigModified = (state: RootState): boolean => {
  return state.solveConfig.voice.data.is_modified;
};

export const selectIsPlaybookConfigModified = (state: RootState): boolean =>
  state.solveConfig.playbook.data.is_modified;

export const selectIsSlackConfigModified = (state: RootState): boolean =>
  state.solveConfig.slack.data.is_modified;

export const selectIsApiConfigModified = (state: RootState): boolean =>
  state.solveConfig.api.data.is_modified;

export const selectCanPublishVoiceConfig = (state: RootState): boolean => {
  const voiceConfig = state.solveConfig.voice.data;

  const defaultHandoffNumber = voiceConfig.default_handoff_number;
  const isHandoffNumberValid = Boolean(
    !defaultHandoffNumber || isPhoneNumberValid(defaultHandoffNumber),
  );

  const isDisclaimerTextValid =
    !voiceConfig.is_disclaimer_enabled ||
    voiceConfig.disclaimer_text.trim() !== '';

  return isHandoffNumberValid && isDisclaimerTextValid;
};

export const selectIsWebCallConfigModified = (state: RootState): boolean => {
  return state.solveConfig.webCall.isModified;
};

export const selectLiveVersionExistsWebCall = (state: RootState): boolean => {
  return state.solveConfig.webCall.liveVersionExists;
};

export const selectWebCallData = (
  state: RootState,
): WebCallConfigData | null => {
  return state.solveConfig.webCall.data;
};

export const selectCanPublishWebCallConfig = (_state: RootState): boolean => {
  // For now, always publishable (no validation rules yet - Phases 6-10 add them)
  return true;
};

/**
 * !Keep up to date with solve-ui selectIsWhiteColorLegible
 */
const selectIsWhiteColorLegible = (state: RootState): boolean =>
  tinycolor.readability(
    state.solveConfig.widget.data.theme_color,
    theme.palette.colors.white,
  ) >= 4.5;

/**
 * !Keep up to date with solve-ui selectLegibleTextColor
 */
export const selectLegibleTextColor = (state: RootState): string =>
  selectIsWhiteColorLegible(state)
    ? theme.palette.colors.white
    : theme.palette.text.primary;

/**
 * !Keep up to date with solve-ui selectLegibleButtonOutlineColor
 */
export const selectLegibleButtonOutlineColor = (state: RootState): string => {
  const themeColor = state.solveConfig.widget.data.theme_color;

  const candidates = [
    ...tinycolor(themeColor).monochromatic(),
    tinycolor(themeColor).lighten(50),
    tinycolor(themeColor).darken(50),
    ...tinycolor(themeColor).triad(),
    ...tinycolor(themeColor).tetrad(),
    ...tinycolor(themeColor).splitcomplement(),
    tinycolor(themeColor).greyscale(),
  ];

  return (
    candidates
      .find(
        candidate =>
          tinycolor.readability(candidate, theme.palette.colors.white) >= 3 &&
          tinycolor.readability(candidate, themeColor) >= 3,
      )
      ?.toRgbString() || theme.palette.text.primary
  );
};

/**
 * !Keep up to date with solve-ui selectThemeColor
 */
export const selectLegibleElementColor = (state: RootState): string =>
  selectIsWhiteColorLegible(state)
    ? state.solveConfig.widget.data.theme_color
    : theme.palette.colors.grey[800];

/**
 * !Keep up to date with solve-ui selectLegibleTextColorOnLegibleElementColor
 */
export const selectLegibleTextColorOnLegibleElementColor = (
  state: RootState,
): string => {
  const elementColor = selectLegibleElementColor(state);
  const readability = tinycolor.readability(
    elementColor,
    theme.palette.colors.white,
  );
  if (readability >= 4.5) {
    return theme.palette.colors.white;
  }
  return theme.palette.text.primary;
};

export const selectIsWidgetValidationFailing = (
  state: Pick<RootState, 'solveConfig'>,
): boolean => selectWarningModalsVisibility(state).includes(true);

export const selectWarningModalsVisibility = (
  state: Pick<RootState, 'solveConfig'>,
): boolean[] => {
  return [
    !state.solveConfig.widget.data.greeting_line.trim(),
    !state.solveConfig.widget.data.proactive_chat_greeting?.trim() &&
      state.solveConfig.widget.data.display_proactive_chat,
    Boolean(
      validateKnowledgeConfigurationPrompt({
        prompt: state.solveConfig.widget.data.knowledge_configuration_prompt,
        shouldUseLongerNoteLength: state.solveConfig.isLongerNoteEnabled,
      }),
    ),
    state.solveConfig.widget.data.intro_config.title.enabled &&
      !state.solveConfig.widget.data.intro_config.title.value.trim(),
    state.solveConfig.widget.data.intro_config.subtitle.enabled &&
      !state.solveConfig.widget.data.intro_config.subtitle.value.trim(),
    state.solveConfig.widget.data.banner_image_config.is_enabled &&
      !isUrlValid(state.solveConfig.widget.data.banner_image_config.link),
    state.solveConfig.widget.data.general_instruction_config &&
      !validateGeneralInstructionConfig(
        state.solveConfig.widget.data?.general_instruction_config,
      ),
  ];
};

export const selectIsPlaybookValidationFailing = (
  state: Pick<RootState, 'solveConfig'>,
): boolean => selectPlaybookWarningModalsVisibility(state).includes(true);

export const selectPlaybookWarningModalsVisibility = (
  state: Pick<RootState, 'solveConfig'>,
): boolean[] => {
  return [
    Boolean(
      validateKnowledgeConfigurationPrompt({
        prompt: state.solveConfig.playbook.data.knowledge_configuration_prompt,
        shouldUseLongerNoteLength: state.solveConfig.isLongerNoteEnabled,
      }),
    ),
  ];
};

export const selectIsSlackValidationFailing = (
  state: Pick<RootState, 'solveConfig'>,
): boolean => selectSlackWarningModalsVisibility(state).includes(true);

export const selectSlackWarningModalsVisibility = (
  state: Pick<RootState, 'solveConfig'>,
): boolean[] => {
  return [
    state.solveConfig.slack.data?.general_instruction_config &&
      !validateGeneralInstructionConfig(
        state.solveConfig.slack.data?.general_instruction_config,
      ),
  ];
};

export const selectIsApiValidationFailing = (
  state: Pick<RootState, 'solveConfig'>,
): boolean => selectApiWarningModalsVisibility(state).includes(true);

export const selectApiWarningModalsVisibility = (
  state: Pick<RootState, 'solveConfig'>,
): boolean[] => {
  return [
    state.solveConfig.api.data?.general_instruction_config &&
      !validateGeneralInstructionConfig(
        state.solveConfig.api.data?.general_instruction_config,
      ),
  ];
};

export const selectIsVoiceValidationFailing = (
  state: Pick<RootState, 'solveConfig'>,
): boolean => selectVoiceWarningModalsVisibility(state).includes(true);

export const selectVoiceWarningModalsVisibility = (
  state: Pick<RootState, 'solveConfig'>,
): boolean[] => {
  return [
    state.solveConfig.voice.data?.general_instruction_config &&
      !validateGeneralInstructionConfig(
        state.solveConfig.voice.data?.general_instruction_config,
      ),
  ];
};

export const selectIsWarningModalVisible = (state: RootState): boolean => {
  return state.solveConfig.validationState.isWarningModalVisible;
};

export const selectBrandId = (state: RootState): string =>
  state.solveConfig.brandId;

export const selectIsLoadingInitialConfig = (state: RootState): boolean =>
  state.solveConfig.isLoadingInitialConfig;

export const selectPlaybookConfiguration = (state: RootState) =>
  state.solveConfig.playbook.data;

export const selectPlaybookIsLoadingInitialConfig = (state: RootState) =>
  state.solveConfig.playbook.isLoadingInitialConfig;

export const selectEmailSettingsFormValues = (state: RootState) =>
  state.solveConfig.emailSettingsFormValues;

export const selectEmailToneOfVoiceConfig = (state: RootState) =>
  state.solveConfig.emailToneOfVoiceConfig;

export const selectEmailKnowledgeConfigurationPrompt = (state: RootState) =>
  state.solveConfig.emailKnowledgeConfigurationPrompt;

export const selectEmailConfigurationFields = (state: RootState) =>
  state.solveConfig.emailConfigurationFields;

export const selectGeneralConfig = (state: RootState) =>
  state.solveConfig.playbook.data.general_config;

export default solveConfigSlice.reducer;
export const {
  addBoughtPhoneNumber,
  addBoughtPhoneNumbers,
  hideWarningModal,
  removeBoughtPhoneNumber,
  setApiGeneralInstructionConfig,
  setAssistGeneralInstructionConfig,
  setAssistGeneralInstructionConfigGeneratedReply,
  setAttachmentAnytimeEnabled,
  setBannerImageConfig,
  setBrandId,
  setCsatConfigByKey,
  setDictationEnabled,
  setEmailConfigurationFields,
  setEmailGeneralInstructionConfig,
  setEmailKnowledgeConfigurationPrompt,
  setEmailSettingsFormValues,
  setEmailToneOfVoiceConfig,
  setErrorHandlingConfigByKey,
  setExcludePrivateArticles,
  setFont,
  setFreeFormIntentDetectionEnabled,
  setGeneralConfig,
  setGeneralInstructionConfig,
  setGreetingLine,
  setIntentOptions,
  setIntroImageEnabled,
  setIntroSubtitleEnabled,
  setIntroSubtitleValue,
  setIntroTitleEnabled,
  setIntroTitleValue,
  setIsGenericFieldsLoading,
  setIsHighFrequencyFieldsLoading,
  setIsSwitchingConfig,
  setKnowledgeConfigurationPrompt,
  setKnowledgeConfigurationPromptPlaybook,
  setLaunchStyle,
  setLiveVersionIsActive,
  setManuallySelectedTopIntents,
  setNumTopIntents,
  setPlacement,
  setPrivacyConsentConfigurationByKey,
  setProactiveChatGreeting,
  setQuickFeedbackConfigByKey,
  setReplyGenerationPromptPlaybook,
  setRestartClearsHandoffTranscript,
  setSelectedPhoneNumber,
  setSentimentConfigByKey,
  setSize,
  setSlackGeneralInstructionConfig,
  setSolveConfigInitialized,
  setStartUploadingImage,
  setTabName,
  setTagSelectionEnabled,
  setThemeColor,
  setToneOfVoiceConfig,
  setToneOfVoiceConfigApi,
  setToneOfVoiceConfigPlaybook,
  setToneOfVoiceConfigPlaybookGeneratedReply,
  setToneOfVoiceConfigSlack,
  setVoiceConfiguration,
  setVoiceCsatConfigByKey,
  setVoiceDataByKey,
  setWebCallData,
  setWebCallDataByKey,
  setWidgetArticleSearchEnabled,
  setWidgetDataByKey,
  showWarningModal,
  swapManuallySelectedTopIntents,
} = solveConfigSlice.actions;
